import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, RefreshCw, AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Thermometer } from 'lucide-react';
import { getApiHeaders } from '../../utils/apiHelpers';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const FasciaGroup = ({ gruppo, expanded, onToggle }) => (
  <div className="border-b border-gray-100 last:border-b-0">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
      data-testid={`fascia-toggle-${gruppo.fascia.replace(':', '')}`}
    >
      <div className="flex items-center gap-2">
        {expanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
        <Clock className="h-4 w-4 text-cyan-600" />
        <span className="font-semibold text-gray-900">{gruppo.fascia}</span>
        {gruppo.manuale && <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium">manuale</span>}
        <span className="text-xs text-gray-500">{gruppo.righe.length} frigoriferi</span>
      </div>
      {gruppo.anomalie > 0 ? (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold">
          <AlertTriangle className="h-3 w-3" /> {gruppo.anomalie} anomali{gruppo.anomalie === 1 ? 'a' : 'e'}
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-green-600 text-xs font-semibold">
          <CheckCircle2 className="h-3.5 w-3.5" /> tutto OK
        </span>
      )}
    </button>
    {expanded && (
      <div className="overflow-x-auto bg-gray-50/50">
        <table className="w-full text-sm">
          <tbody>
            {gruppo.righe.map((m) => (
              <tr key={m.id} className={`border-t border-gray-100 ${m.anomala ? 'bg-red-50' : ''}`}>
                <td className="px-4 py-2 pl-10 font-medium text-gray-900">{m.frigorifero_nome}</td>
                <td className={`px-4 py-2 font-bold ${m.anomala ? 'text-red-600' : 'text-gray-900'}`}>{m.temperatura}°C</td>
                <td className="px-4 py-2 text-gray-500 hidden sm:table-cell">{m.temp_min}° / {m.temp_max}°</td>
                <td className="px-4 py-2 text-right">
                  {m.anomala ? (
                    <span className="inline-flex items-center gap-1 text-red-600 text-xs font-semibold"><AlertTriangle className="h-3.5 w-3.5" /> ANOMALIA</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-green-600 text-xs font-semibold"><CheckCircle2 className="h-3.5 w-3.5" /> OK</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const MonitoraggioTemperature = () => {
  const oggi = new Date().toISOString().split('T')[0];
  const [data, setData] = useState(oggi);
  const [monitoraggi, setMonitoraggi] = useState([]);
  const [anomalie, setAnomalie] = useState(0);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [expanded, setExpanded] = useState({});

  const carica = useCallback(async (giorno) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/gestione/haccp/monitoraggio-temperature?data=${giorno}`, { headers: getApiHeaders() });
      setMonitoraggi(res.data.monitoraggi || []);
      setAnomalie(res.data.anomalie || 0);
    } catch (e) {
      setMonitoraggi([]);
      setAnomalie(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carica(data); }, [data, carica]);

  const controlloManuale = async () => {
    setRunning(true);
    try {
      const res = await axios.post(`${API}/gestione/haccp/monitoraggio-temperature/esegui`, {}, { headers: getApiHeaders() });
      const { letture, anomalie: anom, motivo, esclusi, non_raggiungibili: muti } = res.data;
      const esclusiTxt = esclusi?.length ? ` (${esclusi.length} escluso/i: ${esclusi.join(', ')})` : '';
      if (muti?.length > 0) {
        toast.warning(`Sensore non raggiungibile (controlla le pile): ${muti.join(', ')}`, { duration: 10000 });
      }
      if (letture > 0) {
        toast.success(`Controllo completato: ${letture} frigoriferi letti${anom > 0 ? `, ${anom} anomalie rilevate` : ', tutto OK'}${esclusiTxt}`);
      } else if (motivo === 'nessun_sensore') {
        toast.warning('Nessun frigorifero con sensore collegato');
      } else if (motivo === 'tuya_non_configurato') {
        toast.warning('Sensori Tuya non configurati');
      } else if (motivo === 'tutti_esclusi') {
        toast.info(`Nessun controllo: tutti i frigoriferi con sensore sono spenti o in manutenzione${esclusiTxt}`);
      } else {
        toast.warning('Nessuna lettura ottenuta dai sensori');
      }
      setData(oggi);
      await carica(oggi);
    } catch (e) {
      toast.error('Errore durante il controllo manuale');
    } finally {
      setRunning(false);
    }
  };

  // Raggruppa per fascia oraria (ordinate dalla più recente)
  const gruppi = Object.values(
    monitoraggi.reduce((acc, m) => {
      const origine = m.origine || 'automatico';
      const key = `${m.fascia_oraria}|${origine}`;
      if (!acc[key]) acc[key] = { fascia: m.fascia_oraria, manuale: origine === 'manuale', righe: [], anomalie: 0 };
      acc[key].righe.push(m);
      if (m.anomala) acc[key].anomalie += 1;
      return acc;
    }, {})
  ).sort((a, b) => b.fascia.localeCompare(a.fascia));

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6" data-testid="monitoraggio-4h-card">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Thermometer className="h-5 w-5 text-cyan-600" />
          <span className="font-semibold text-gray-900">Controlli Temperature</span>
          {anomalie > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold" data-testid="monitoraggio-anomalie-badge">
              <AlertTriangle className="h-3 w-3" /> {anomalie}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={controlloManuale}
            disabled={running}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-700 disabled:opacity-60 text-white text-sm font-semibold transition-colors"
            data-testid="controllo-manuale-btn"
          >
            <Thermometer className={`h-4 w-4 ${running ? 'animate-pulse' : ''}`} />
            {running ? 'Controllo...' : 'Controlla Ora'}
          </button>
          <input
            type="date"
            value={data}
            max={oggi}
            onChange={(e) => setData(e.target.value)}
            className="border border-gray-300 rounded-lg px-2 py-1 text-sm text-gray-900"
            data-testid="monitoraggio-data-input"
          />
          <button onClick={() => carica(data)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600" data-testid="monitoraggio-refresh-btn">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {gruppi.length === 0 ? (
        <div className="px-4 py-6 text-center text-sm text-gray-500" data-testid="monitoraggio-empty">
          Nessun controllo registrato per questa data.<br />
          I sensori vengono letti alla compilazione automatica del registro — oppure usa "Controlla Ora".
        </div>
      ) : (
        <div data-testid="monitoraggio-fasce">
          {gruppi.map((g) => {
            const k = `${g.fascia}|${g.manuale}`;
            return (
              <FasciaGroup
                key={k}
                gruppo={g}
                expanded={!!expanded[k]}
                onToggle={() => setExpanded((p) => ({ ...p, [k]: !p[k] }))}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MonitoraggioTemperature;
